import { Button, MenuItem } from '@mui/material';
import styles from './styles.module.css';
import DataTable from '../../utils/DataTable';
import ShopServices from '../../../services/ShopServices';
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { TablePagination } from '@mui/material';
import Loader from '../../utils/Loading';
import Info from '../../utils/Alert/Info';
import { AlertMessagesContext } from 'react-alert-messages';
import TextField from '@mui/material/TextField';
import { Grid, Tooltip } from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RenewPlanPopup from '../../popups/RenewPlanPopup';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';
import DealerService from '../../../services/DealerServices';

function RenewShopsPage() {
  const [shops, setShops] = useState();
  const [shopsFiltered, setShopsFiltered] = useState('');
  const [searchText, setSearchText] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [page, setPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [isShowRefreshButton, setIsShowRefreshButton] = useState(false);
  const [filterFrom, setFilterFrom] = useState('0');
  const [filterTo, setFilterTo] = useState('7');
  const [lastAppliedFrom, setLastAppliedFrom] = useState('');
  const [lastAppliedTo, setLastAppliedTo] = useState('');
  const [dateRangeLabel, setDateRangeLabel] = useState('');
  const [detailsPopup, setDetailsPopup] = useState({
    open: false,
    details: {},
    terminal: null,
  });
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState('');

  const { postAlertMessage } = useContext(AlertMessagesContext);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    const _offset = newPage * limit;
    setOffset(_offset);
    getShops({ _offset });
  };

  const isDateFilterChanged = () => {
    return filterFrom !== lastAppliedFrom || filterTo !== lastAppliedTo;
  };

  const handleChangeRowsPerPage = (event) => {
    const _limit = +event.target.value;
    setLimit(_limit);
    getShops({ _limit });
  };

  const handleShopSearch = (value) => {
    if (value !== searchText) {
      setOffset(0);
      setSearchText(value);
      setIsShowRefreshButton(true);
    }
  };
  const getDealers = async () => {
  try {
    const res = await DealerService.getDealers();
    setDealers(res.data);
  } catch (error) {
    postAlertMessage({
      text: error.message,
      type: 'failed',
    });
  }
};

  const getShops = useCallback(
    async ({ _offset = offset, _limit = limit, search = searchText, dealerId = selectedDealer } = {}) => {
      setLoadingIndicator(true);
      try {
        const params = {
          offset: _offset,
          limit: _limit,
          search,
        };

        if (dealerId) {
          params.dealer_id = dealerId;
        }

        if (filterFrom !== '' && filterTo !== '') {
          const today = new Date();

          const fromOffset = parseInt(filterFrom);
          const toOffset = parseInt(filterTo);

          const fromDateObj = new Date(today);
          fromDateObj.setDate(today.getDate() - fromOffset);

          const toDateObj = new Date(today);
          toDateObj.setDate(today.getDate() + toOffset);

          const [fromDateFinal, toDateFinal] =
            fromDateObj < toDateObj ? [fromDateObj, toDateObj] : [toDateObj, fromDateObj];

          params.subscription_end_from_date = fromDateFinal.toISOString().split('T')[0];
          params.subscription_end_to_date = toDateFinal.toISOString().split('T')[0];

          const formatDate = (dateObj) =>
            dateObj.toLocaleDateString('en-IN', { year: 'numeric', month: 'numeric', day: 'numeric' });

          setDateRangeLabel(`Shops Renewing Between  ${formatDate(fromDateFinal)} And ${formatDate(toDateFinal)}`);
        } else {
          setDateRangeLabel('');
        }

        const res = await ShopServices.getShops(params);
        setShopsFiltered(res.data);
        setCount(res.count);
        setShops(res.data);
        setIsShowRefreshButton(false);
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [limit, offset, searchText, postAlertMessage, filterFrom, filterTo, selectedDealer]
  );

  function getRenewPlanForShop(shop) {
    const price = shop.subscription.yearly_price;
    return {
      subscription: {
        name: shop.subscription.name,
        validity: shop.subscription.days,
        price,
      },
      add_ons: [],
      amount: price,
      tax: 0,
      payable: price,
    };
  }

  const headerData = [
    {
      label: 'ID',
      id: 'id',
      type: 'text',
    },
    {
      label: 'Shop name',
      id: 'shop_name',
      type: 'text',
    },
    {
      label: 'Phone',
      id: 'mobile_no',
      type: 'text',
    },
    {
      label: 'Subscription',
      id: 'subscription',
      type: 'callback',
      viewRender: (data) => data?.subscription?.name,
    },
    {
      label: 'Subscription End',
      id: 'subscription_end_date',
      type: 'text',
    },
    {
      label: 'Dealer',
      id: 'dealer_name',
      type: 'callback',
      viewRender: (data) => data?.dealer?.name,
    },
    {
      label: 'Actions',
      id: 'actions',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (shop) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title="Details">
              <VisibilityIcon
                className={styles.iconBtn}
                onClick={() =>
                  setDetailsPopup({
                    open: true,
                    shop,
                    terminal: shop.online_order_terminal,
                    details: getRenewPlanForShop(shop),
                  })
                }
              />
            </Tooltip>
            {shop.mobile_no && (
              <Tooltip title={`Call ${shop.mobile_no}`}>
                <a href={`tel:${shop.mobile_no}`} style={{ textDecoration: 'none' }}>
                  <CallIcon className={styles.iconBtn} />
                </a>
              </Tooltip>
            )}
          </Grid>
        );
      },
    },
  ];

  useEffect(() => {
    setLastAppliedFrom(filterFrom);
    setLastAppliedTo(filterTo);
    getShops({ _offset: 0, _limit: limit });
    getDealers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Loader isOpen={loadingIndicator} />
      <div className={styles.filterSec}>
        <div>
          <h2 style={{ margin: '0', color: '#333' }}>PENDING RENEWALS</h2>
        </div>
        <div className={styles.changeable}>
          <div className={styles.title}>
            <div className={styles.headTitle}>
              <h2 className={styles.subTitle} style={{ visibility: dateRangeLabel ? 'visible' : 'hidden' }}>
                {dateRangeLabel}
              </h2>
            </div>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleShopSearch(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    getShops({ _offset: 0, _limit: limit });
                  }
                }}
                className={styles.searchInput}
                placeholder="Search shops"
              />
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={getShops}
                disabled={isShowRefreshButton === false}
              >
                Search
              </Button>
            </div>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.rangeFilter}>
              <div style={{ paddingBottom: '8px' }}>
                <label className={styles.label}>Date Range</label>
              </div>
              <div className={styles.rangeField}>
                <TextField
                  label="Backward Date"
                  type="number"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  placeholder="Backward Date"
                  className={styles.numberField}
                />
                <TextField
                  label="Forward Date"
                  type="number"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  placeholder="Forward Date"
                  className={styles.numberField}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    getShops({ _offset: 0, _limit: limit });
                    setLastAppliedFrom(filterFrom);
                    setLastAppliedTo(filterTo);
                  }}
                  disabled={!isDateFilterChanged()}
                  className={styles.actionBtn}
                >
                  Apply Date Filter
                </Button>
              </div>
            </div>
            <div className={styles.rangeFilter}>
              <div style={{ paddingBottom: '8px' }}>
                <label className={styles.label}>Dealer</label>
              </div>
              <div className={styles.rangeField}>
                <TextField
                  select
                  value={selectedDealer}
                  onChange={(e) => {
                    setSelectedDealer(e.target.value);
                    getShops({ dealerId: e.target.value, _offset: 0, _limit: limit });
                  }}
                  style={{ minWidth: 150 }}
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="">All Dealers</MenuItem>
                  {dealers.map((dealer) => (
                    <MenuItem key={dealer?.id} value={dealer?.id}>
                      {dealer?.name}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>
          </div>
        </div>
      </div>

      {shops &&
        (shopsFiltered && shopsFiltered.length ? (
          <>
            <DataTable columns={headerData} rows={shopsFiltered || shops} />
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={count}
              rowsPerPage={limit}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        ) : (
          <Info
            title={'No shops to list'}
            content="No shops available to renew with the current filters. Please update your filters."
          />
        ))}

      <RenewPlanPopup
        open={detailsPopup.open}
        onClose={() => setDetailsPopup((dp) => ({ ...dp, open: false }))}
        shop={detailsPopup.shop}
        currency={detailsPopup.shop?.currency || detailsPopup.shop?.currency_symbol || '₹'}
      />


    </div>
  );
}

export default withConsoleBase(RenewShopsPage);
